import React, { useEffect, useRef } from "react";
import { Spinner } from "react-bootstrap";
import ChatMessage from "./ChatMessage";
import AIPortalLogo from "./AIPortalLogo";
import styles from "./ChatWorkspace.module.scss";
import ViewListIcon from "@mui/icons-material/ViewList";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TodayIcon from "@mui/icons-material/Today";
import SummarizeIcon from "@mui/icons-material/Summarize";

const CRM_SUGGESTIONS = [
  {
    id: "list",
    title: "List Enquiries",
    description: "View and filter enquiries by date, status, or source.",
    prompt: "Show me the list of enquiries",
    Icon: ViewListIcon,
  },
  {
    id: "create",
    title: "Create Enquiry",
    description: "Add a new student enquiry with contact details.",
    prompt: "Create an enquiry for a new student",
    Icon: PersonAddIcon,
  },
  {
    id: "update",
    title: "Update Enquiry Status",
    description: "Change the status of an enquiry (e.g. to Contacted).",
    prompt: "Update enquiry status to Contacted",
    Icon: SwapHorizIcon,
  },
  {
    id: "details",
    title: "Get Enquiry Details",
    description: "Fetch full details for a specific enquiry.",
    prompt: "Get details for enquiry",
    Icon: InfoOutlinedIcon,
  },
  {
    id: "today",
    title: "Today's Enquiries",
    description: "See enquiries created today.",
    prompt: "Show me today's enquiries",
    Icon: TodayIcon,
  },
  {
    id: "summarize",
    title: "Summarize & Analyse",
    description: "Summarize uploaded files or ask general questions.",
    prompt: "Summarize the latest uploaded file or answer a general question",
    Icon: SummarizeIcon,
  },
];

export default function ChatWindow({
  messages,
  streaming,
  onRegenerate,
  onContinue,
  onEdit,
  onCopy,
  onReact,
  onQuickPrompt,
  conversationId,
  onFormSubmit,
  onSuggestionClick,
}) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, streaming]);

  const visibleMessages = (messages || []).filter((m) => m.role !== "tool");

  return (
    <div className={styles.messages}>
      {visibleMessages?.length ? (
        visibleMessages.map((message) => (
          <ChatMessage
            key={message.id || `${message.role}-${message.created_at}`}
            message={message}
            onRegenerate={onRegenerate}
            onContinue={onContinue}
            onEdit={onEdit}
            onCopy={onCopy}
            onReact={onReact}
            conversationId={conversationId}
            onFormSubmit={onFormSubmit}
            onSuggestionClick={onSuggestionClick}
          />
        ))
      ) : (
        <div className={styles.welcomeWrap}>
          <div className={styles.welcomeIcon}>
            <AIPortalLogo size="large" className={styles.welcomeAbyssLogo} />
          </div>
          <h2 className={styles.welcomeTitle}>Welcome To Abyss</h2>
          <p className={styles.welcomeSubtitle}>
            Get started by describing a task and Abyss can do the rest. Not sure where to start?
          </p>
          <div className={styles.welcomeSuggestionsGrid}>
            {CRM_SUGGESTIONS.map(({ id, title, description, prompt, Icon }) => (
              <button
                key={id}
                type="button"
                className={styles.welcomeSuggestionCard}
                onClick={() => onQuickPrompt?.(prompt)}
              >
                <div className={styles.welcomeSuggestionIcon}>
                  <Icon sx={{ fontSize: 22 }} />
                </div>
                <span className={styles.welcomeSuggestionTitle}>{title}</span>
                <span className={styles.welcomeSuggestionDesc}>{description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {streaming && (
        <div className={styles.streamingIndicator}>
          <Spinner size="sm" animation="border" className="me-2" />
          typing...
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
